import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTrackContext } from "../../../../hooks/useTrackContext";
import { ROUTES, TRACK_OPTIONS } from "../../../../consts";
import { TAgeFilter, IGCollectionsProduct } from "../../../../types";
import ProgressBar from "../../../ProgressiveBar";
import { useNavigate } from "react-router-dom";
import DisplayPopUp from "./AgeGroupsPopup";
import NoUnder12AvailablePopup from "./NoUnder12AvailablePopup";
import MiniCart from "../../../Cart/MiniCart";
import { GET_METAOBJECT_BY_HANDLE } from "../../../../graphqlQueries";
import { SHOPIFY_STOREFRONT_GRAPHQL_API, SHOPIFY_STOREFRONT_TOKEN } from "../../../../config";
import { MainTrackUnder12Video, MainTrack12To15Video, MainTrack16PlusVideo } from "../../../../consts/assetUrls";
import BottomSummary from "../../../BottomSummary";
import { fetchCollectionsByHandle } from "../../../../utils/productOperations";

const SelectAgeGroup: React.FC = () => {
  const { dispatch, mainTrackSelection, productRenderedLocation, appHostedAt, metaObjectMainTrackSectionConfig } = useTrackContext();
  const { ageGroup } = mainTrackSelection;
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const { ageGroupSection } = metaObjectMainTrackSectionConfig;
  const [nextRoute, setNextRoute] = useState<string>("/" + ROUTES.MAIN_TRACK.RACE_TYPE);
  const navigate = useNavigate();

  const [isPopupShowingSixtenPlus, setIsPopupShowingSixtenPlus] = useState<{ showFor: TAgeFilter | null; isPopupDisplaying: boolean }>({
    showFor: null,
    isPopupDisplaying: true,
  });
  const [isPopupShowingUnder12, setIsPopupShowingUnder12] = useState<{ showFor: TAgeFilter | null; isPopupDisplaying: boolean }>({
    showFor: null,
    isPopupDisplaying: true,
  });
  const [isPopupShowing12To15, setIsPopupShowing12To15] = useState<{ showFor: TAgeFilter | null; isPopupDisplaying: boolean }>({
    showFor: null,
    isPopupDisplaying: true,
  });
  const [isNoUnder12AvailablePopupShowing, setIsNoUnder12AvailablePopupShowing] = useState<boolean>(false);
  const [collectionProducts, setCollectionProducts] = useState<IGCollectionsProduct[]>([]);

  const handleShowPopup = () => {
    if (ageGroup === "16+") {
      setIsPopupShowingSixtenPlus((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
    if (ageGroup === "12-15") {
      setIsPopupShowing12To15((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
    if (ageGroup === "Under 12") {
      setIsPopupShowingUnder12((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
  };

  const handlePopupDisplay = () => {
    if (ageGroup === "16+") {
      setIsPopupShowingSixtenPlus((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
    if (ageGroup === "12-15") {
      setIsPopupShowing12To15((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
    if (ageGroup === "Under 12") {
      setIsPopupShowingUnder12((prevState) => ({ ...prevState, isPopupDisplaying: !prevState.isPopupDisplaying }));
    }
  };

  const checkUnder12Availability = useCallback(() => {
    const hasUnder12Sessions = collectionProducts.some((cProduct: IGCollectionsProduct) => {
      return cProduct.variants.nodes.some((variant) => {
        return (
          variant.availableForSale &&
          variant.quantityAvailable > 0 &&
          variant.selectedOptions.find((option) => 
            option.name.startsWith("Age Requirement") && 
            option.value.toLowerCase().includes("ages under 12")
          )
        );
      });
    });
    return hasUnder12Sessions;
  }, [collectionProducts]);

  const dispatchAgeUpdate = useCallback(
    (updateagegroup: TAgeFilter) => {
      // Check if Under 12 is selected and not available
      if (updateagegroup === "Under 12" && !checkUnder12Availability()) {
        setIsNoUnder12AvailablePopupShowing(true);
        return;
      }

      let newNextRoute = nextRoute;

      if (updateagegroup === "12-15" || updateagegroup === "Under 12") {
        newNextRoute = "/" + ROUTES.MAIN_TRACK.SEATS_RACES;
      } else {
        newNextRoute = "/" + ROUTES.MAIN_TRACK.RACE_TYPE;
      }

      if (updateagegroup === "16+" && !isPopupShowingSixtenPlus.showFor) {
        setIsPopupShowingSixtenPlus({ showFor: updateagegroup, isPopupDisplaying: true });
      }

      if (updateagegroup === "12-15" && !isPopupShowing12To15.showFor) {
        setIsPopupShowing12To15({ showFor: updateagegroup, isPopupDisplaying: true });
      }

      if (updateagegroup === "Under 12" && !isPopupShowingUnder12.showFor) {
        setIsPopupShowingUnder12({ showFor: updateagegroup, isPopupDisplaying: true });
      }

      dispatch({
        type: "SET_AGE_GROUP",
        payload: {
          selectedTrack: "MAIN_TRACK",
          ageGroup: updateagegroup,
        },
      });

      if (ageGroup === updateagegroup) {
        return navigate(newNextRoute);
      }
    },
    [ageGroup, dispatch, isPopupShowing12To15.showFor, isPopupShowingSixtenPlus.showFor, isPopupShowingUnder12.showFor, navigate, nextRoute, checkUnder12Availability]
  );

  useEffect(() => {
    let newNextRoute = nextRoute;
    if (ageGroup === "16+") {
      newNextRoute = "/" + ROUTES.MAIN_TRACK.RACE_TYPE;
    } else if (ageGroup === "12-15") {
      newNextRoute = "/" + ROUTES.MAIN_TRACK.SEATS_RACES;
    } else if (ageGroup === "Under 12") {
      newNextRoute = "/" + ROUTES.MAIN_TRACK.SEATS_RACES;
    }
    setNextRoute(newNextRoute);
  }, [ageGroup, nextRoute]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SHOPIFY_STOREFRONT_GRAPHQL_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: GET_METAOBJECT_BY_HANDLE,
            variables: {
              handle: "terms_and_conditions_main_track",
              type: "information_react_component",
            },
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const fields = data.data.metaobject.fields;
        const [value] = fields;
        dispatch({ type: "SET_TERMS_AND_CONDITIONS_CONTENT", payload: { termsAndConditionsContent: value.value } });
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        const products = await fetchCollectionsByHandle("handle:main-track");
        if (products) {
          setCollectionProducts(products);
        }
      } catch (error) {
        console.error("Error fetching collection products:", error);
      }
    };

    fetchCollectionData();
  }, []);

  const showPopupAgeGroup: boolean = useMemo(() => {
    return (
      !!(isPopupShowingSixtenPlus && isPopupShowingSixtenPlus.showFor && isPopupShowingSixtenPlus.isPopupDisplaying) ||
      !!(isPopupShowing12To15 && isPopupShowing12To15.showFor && isPopupShowing12To15.isPopupDisplaying) ||
      !!(isPopupShowingUnder12 && isPopupShowingUnder12.showFor && isPopupShowingUnder12.isPopupDisplaying)
    );
  }, [isPopupShowingSixtenPlus, isPopupShowing12To15, isPopupShowingUnder12]);

  const overSixtenStyleObject = useMemo(() => {
    return {
      videoUrl: ageGroupSection?.sixteenPlusTileVideoUrl || MainTrack16PlusVideo,
      width: "100%",
      height: "100%",
      borderRadius: "22px",
    };
  }, [ageGroupSection]);

  const twelveToFifteenStyleObject = useMemo(() => {
    return {
      videoUrl: ageGroupSection?.twelveToFifteenTileVideoUrl || MainTrack12To15Video,
      width: "100%",
      height: "100%",
      borderRadius: "22px",
    };
  }, [ageGroupSection]);

  const underTwelveStyleObject = useMemo(() => {
    return {
      videoUrl: ageGroupSection?.underTwelveTileVideoUrl || MainTrackUnder12Video,
      width: "100%",
      height: "100%",
      borderRadius: "22px",
    };
  }, [ageGroupSection]);

  return (
    <>
      <ProgressBar maxValue={100} currentValue={windowWidth <= 768 ? 48 : 28} />
      {productRenderedLocation === "external" && <MiniCart />}
      {/* <span className="tw-block tw-text-[18px] lg:tw-text-[24px] tw-text-[#F609FF] tw-font-[700] tw-mt-[30px] tw-text-center">Main Track</span> */}
      {/* <div className="tw-text-white tw-text-center tw-text-[24px] tw-font-extrabold">Select your Age Group</div> */}

      <div className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"}`}>
        {!appHostedAt && appHostedAt !== "bosshunting" ? (
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-[30px] lg:tw-gap-[50px] tw-my-[30px] lg:tw-mt-[120px] lg:tw-mb-[40px]">
            <div className="hover:tw-cursor-pointer tw-mx-auto lg:tw-mx-0 lg:tw-mr-auto" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN)}>
              <div
                className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                style={
                  ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN
                    ? {
                        padding: "4px",
                        background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                      }
                    : {}
                }
              >
                <div className="tw-h-full tw-w-full tw-rounded-[22px] tw-relative">
                  <video
                    className="tw-h-full tw-w-full tw-rounded-[22px] tw-object-cover"
                    src={overSixtenStyleObject.videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    style={{ pointerEvents: "none" }}
                  />
                  <div className="tw-absolute tw-inset-0" />
                  <div className="tw-absolute tw-inset-0 tw-pointer-events-none" />
                  <div
                    className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-bg-indigo-900 tw-rounded-[25px] ${
                      ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                    }`}
                  >
                    <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                      <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px] tw-text-white">16+</span>
                      <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold tw-text-white">Years</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hover:tw-cursor-pointer tw-mx-auto" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN)}>
              <div
                className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                style={
                  ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN
                    ? {
                        padding: "4px",
                        background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                      }
                    : {}
                }
              >
                <div className="tw-h-full tw-w-full tw-rounded-[22px] tw-relative">
                  <video
                    className="tw-h-full tw-w-full tw-rounded-[22px] tw-object-cover"
                    src={twelveToFifteenStyleObject.videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    style={{ pointerEvents: "none" }}
                  />
                  <div className="tw-absolute tw-inset-0" />
                  <div className="tw-absolute tw-inset-0 tw-pointer-events-none" />
                  <div
                    className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-bg-indigo-900 tw-rounded-[25px] ${
                      ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                    }`}
                  >
                    <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                      <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px] tw-text-white">12-15</span>
                      <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold tw-text-white">Years</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hover:tw-cursor-pointer tw-mx-auto lg:tw-mx-0 lg:tw-ml-auto" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE)}>
              <div
                className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                style={
                  ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE
                    ? {
                        padding: "4px",
                        background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                      }
                    : {}
                }
              >
                <div className="tw-h-full tw-w-full tw-rounded-[22px] tw-relative">
                  <video
                    className="tw-h-full tw-w-full tw-rounded-[22px] tw-object-cover"
                    src={underTwelveStyleObject.videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    style={{ pointerEvents: "none" }}
                  />
                  <div className="tw-absolute tw-inset-0" />
                  <div className="tw-absolute tw-inset-0 tw-pointer-events-none" />
                  <div
                    className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-bg-indigo-900 tw-rounded-[25px] ${
                      ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                    }`}
                  >
                    <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                      <span className="tw-text-[16px] lg:tw-text-[24px] tw-font-[600] tw-text-white">Under</span>
                      <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px] tw-text-white">12</span>
                      <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold tw-text-white">Years</span>
                      <span className="tw-text-[16px] lg:tw-text-[20px] tw-font-[600] tw-text-white">(140cm +)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* this layout is only for bosshunting */}
            {ageGroup === null && (
              <div className="hover:tw-cursor-pointer tw-flex tw-justify-center" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN)}>
                <div
                  className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                  style={
                    ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN
                      ? {
                          padding: "4px",
                          background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                        }
                      : {}
                  }
                >
                  <div className={`tw-bg-cover tw-bg-center tw-h-full tw-w-full tw-rounded-[22px] ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-bg-[#2F1160]" : ""}`}>
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-bg-[#393B41] tw-rounded-[25px] ${
                        ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                      }`}
                    >
                      <span className={`hover:tw-cursor-text ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                        <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                          <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px]">16+</span>
                          <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold">Years</span>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {ageGroup && ageGroup === "16+" && (
              <div className="hover:tw-cursor-pointer tw-flex tw-justify-center" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN)}>
                <div
                  className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                  style={
                    ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN
                      ? {
                          padding: "4px",
                          background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                        }
                      : {}
                  }
                >
                  <div className={`tw-bg-cover tw-bg-center tw-h-full tw-w-full tw-rounded-[22px] ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-bg-[#2F1160]" : ""}`}>
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-bg-[#393B41] tw-rounded-[25px] ${
                        ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                      }`}
                    >
                      <span className={`hover:tw-cursor-text ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                        <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                          <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px]">16+</span>
                          <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold">Years</span>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ageGroup && ageGroup === "12-15" && (
              <div className="hover:tw-cursor-pointer tw-flex tw-justify-center" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN)}>
                <div
                  className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                  style={
                    ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN
                      ? {
                          padding: "4px",
                          background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                        }
                      : {}
                  }
                >
                  <div
                    className={`tw-bg-cover tw-bg-center tw-h-full tw-w-full tw-rounded-[22px] ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN ? "tw-bg-[#2F1160]" : ""}`}
                  >
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-bg-[#393B41] tw-rounded-[25px] ${
                        ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                      }`}
                    >
                      <span className={`hover:tw-cursor-text ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                        <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                          <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px]">12-15</span>
                          <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold">Years</span>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {ageGroup && ageGroup === "Under 12" && (
              <div className="hover:tw-cursor-pointer tw-flex tw-justify-center" onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE)}>
                <div
                  className="tw-h-[165px] tw-w-[280px] lg:tw-h-[380px] tw-rounded-[25px] tw-overflow-hidden"
                  style={
                    ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE
                      ? {
                          padding: "4px",
                          background: "linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)",
                        }
                      : {}
                  }
                >
                  <div className={`tw-bg-cover tw-bg-center tw-h-full tw-w-full tw-rounded-[22px] ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE ? "tw-bg-[#2F1160]" : ""}`}>
                    <div
                      className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-bg-[#393B41] tw-rounded-[25px] ${
                        ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE ? "tw-bg-opacity-0" : "tw-bg-opacity-50"
                      }`}
                    >
                      <span className={`hover:tw-cursor-text ${ageGroup === TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE ? "tw-text-white" : "tw-text-[#C2C0C3]"}`}>
                        <div className="tw-flex tw-flex-col tw-text-center tw-leading-[1.2]">
                          <span className="tw-text-[16px] lg:tw-text-[24px] tw-font-[600]">Under</span>
                          <span className="tw-font-[700] tw-text-[36px] lg:tw-text-[64px]">12</span>
                          <span className="tw-text-[18px] lg:tw-text-[30px] tw-font-semibold">Years</span>
                          <span className="tw-text-[16px] lg:tw-text-[20px] tw-font-[600]">(140cm +)</span>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className={`${windowWidth >= 640 ? "tw-flex tw-justify-center tw-items-center" : ""} tw-gap-[10px] tw-my-[20px]`}>
              <button
                type="button"
                onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.OVERSIXTEEN)}
                style={{ width: "200px" }}
                className={`tw-rounded-[100px] tw-block ${windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"} tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                  ageGroup && ageGroup === "16+" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                }`}
              >
                16+ Years
              </button>
              <button
                type="button"
                onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.TWELVETOFIFTEEN)}
                style={{ width: "200px" }}
                className={`tw-rounded-[100px] tw-block ${
                  windowWidth >= 640 ? "tw-mx-[0] tw-my-[0]" : "tw-mx-auto"
                } tw-text-center tw-my-[10px] tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                  ageGroup && ageGroup === "12-15" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                }`}
              >
                12 to 15 Years
              </button>
              <button
                type="button"
                onClick={() => dispatchAgeUpdate(TRACK_OPTIONS.AGE_OPTIONS.UNDERTWELVE)}
                style={{ width: "200px" }}
                className={`tw-rounded-[100px] tw-block ${windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"} tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                  ageGroup && ageGroup === "Under 12" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                }`}
              >
                Under 12 Years
              </button>
            </div>
            {ageGroup !== null && (
              <div className={`tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]`}>
                <span className={`tw-text-[white] tw-font-[700] tw-text-center tw-flex`}>You are selected {ageGroup} age group</span>
              </div>
            )}
          </>
        )}

        {ageGroup === null && (
          <>
            <div className={`tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]`}>
              <span className={`tw-text-[red] tw-font-[700] tw-text-center tw-flex`}>Please select your age group to continue</span>
            </div>
          </>
        )}

        <div className="tw-flex tw-justify-center tw-items-center tw-gap-[30px] tw-mb-[60px] md:tw-mb-0">
          {(ageGroup === "16+" || ageGroup === "12-15" || ageGroup === "Under 12") && (
            <span className="tw-relative tw-flex tw-justify-center tw-items-center hover:tw-cursor-pointer" onClick={() => handleShowPopup()}>
              <svg xmlns="http://www.w3.org/2000/svg" width="55" height="55" viewBox="0 0 58 57" fill="none">
                <path
                  d="M57.4485 28.5001C57.4485 44.215 44.7122 56.9485 29.0001 56.9485C13.2881 56.9485 0.551758 44.215 0.551758 28.5001C0.551758 12.7909 13.2881 0.0517578 29.0001 0.0517578C44.7122 0.0517578 57.4485 12.7909 57.4485 28.5001Z"
                  fill="#F609FF"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M30.9567 27.0478C30.9567 25.9256 30.0729 25.0105 28.9891 25.0105C27.9052 25.0105 27.0215 25.9256 27.0215 27.0478V37.3392C27.0215 38.4614 27.9052 39.3765 28.9891 39.3765C30.0729 39.3765 30.9567 38.4614 30.9567 37.3392V27.0478ZM27.0326 19.6603C27.0326 20.7826 27.9118 21.6976 28.9889 21.6976C30.0975 21.6976 30.979 20.7826 30.979 19.6603C30.979 18.5381 30.0975 17.623 29.0114 17.623C27.9208 17.623 27.0326 18.5381 27.0326 19.6603Z"
                  fill="white"
                />
              </svg>
            </span>
          )}
          <DisplayPopUp handleClosePopup={() => handlePopupDisplay()} isPopupShowing={showPopupAgeGroup} />
        </div>
      </div>
      <div className="tw-w-full lg:tw-h-[126px] tw-p-[30px]">
        <div className="tw-flex tw-justify-center tw-items-center">
          <div
            className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"} tw-relative tw-flex tw-justify-end tw-items-center lg:tw-block`}
          >
          </div>
        </div>
      </div>
      <NoUnder12AvailablePopup 
        handleClosePopup={() => setIsNoUnder12AvailablePopupShowing(false)} 
        isPopupShowing={isNoUnder12AvailablePopupShowing}
        numberOfSeats={mainTrackSelection.racesSeats.numberOfSeats}
        numberOfRaces={mainTrackSelection.racesSeats.numberOfRaces}
      />
      <BottomSummary previousInfo={{ pageName: "Game Selection", pagePath: "/" }} nextInfo={{ pageName: "Continue", pagePath: nextRoute, isActive: ageGroup !== null }} />
    </>
  );
};

export default SelectAgeGroup;
